import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import {
  captureRect,
  clampRect,
  findCaptureTarget,
  findUncapturableNodes,
  isCaptureDebugEnabled,
  normalizeRect
} from "components/util/helpers/screenshot_helper/ScreenshotHelper.lib.capture";
import ScreenshotHelperView from "components/util/helpers/screenshot_helper/ScreenshotHelper.view";

// Screenshot helper: the user arms a capture mode and drags a region over the
// rendered HTML. What is framed is screenshotted and a comment box opens next to it.
//
//   <ScreenshotHelper>
//     <App />
//   </ScreenshotHelper>
//
//   const { takeScreenshot } = useScreenshotHelper();
//   const onClickReport = async () => {
//     const result = await takeScreenshot();
//     if (!result) return;                 // the user aborted
//     send(result.screenshot, result.annotations);
//   };
//
// This is the ONLY implementation. Custom views do not carry their own and have
// nothing to wrap: `sbv.useScreenshotHelper` asks over the bridge and Main arms
// this overlay over the whole flow layout — so a topbar can point at the content
// view beside it (see Main.sub.ScreenshotPortal.view.js).

const MIN_REGION_SIZE = 8;

const ScreenshotHelperContext = createContext(null);

// Names the nodes that cannot be rasterized, for whoever is debugging a capture that
// failed. Detached on purpose: it is slower than the capture it is explaining.
const reportUncapturableNodes = (rootNode) =>
  findUncapturableNodes(rootNode)
    .then((culprits) =>
      console.error("[ScreenshotHelper] nodos que rompen la captura:", culprits))
    .catch((error) =>
      console.error("[ScreenshotHelper] el bisect falló", error));

// ─── The overlay ───
//
// Armed for as long as it is mounted, over any DOM node — a subtree of this app,
// or the container of a custom view. Everything is measured in `rootNode`'s own
// realm, so the same component works in-realm and inside a same-origin iframe.
const ScreenshotHelperOverlay = ({
  backgroundColor = "auto",
  captureScale = 2,
  frameElement = null,
  rootNode,
  onCancel,
  onCapture,
}) => {

  const overlayRef = useRef(null);
  // Drag origin lives in a ref because it is read inside pointer handlers that
  // must not wait for a re-render to see it.
  const dragOriginRef = useRef(null);

  const [isCapturing, setIsCapturing] = useState(false);
  const [dragRect, setDragRect] = useState(null);
  // What the user picked: set the moment they release, so the comment box opens
  // right away like the image annotator's does.
  const [region, setRegion] = useState(null);
  // The screenshot of that region, which arrives a beat later.
  const [capture, setCapture] = useState(null);
  // Why it never arrived, shown in the popup so a broken capture is diagnosable
  // without opening the console.
  const [captureError, setCaptureError] = useState(null);
  // Bumped to run the capture again over the same selection. A failure is not always the
  // same failure twice — a resource that timed out is the usual one — and re-picking the
  // exact region by hand to find out is not a reasonable thing to ask.
  const [captureAttempt, setCaptureAttempt] = useState(0);
  const [comment, setComment] = useState("");

  const targetDocument = rootNode.ownerDocument;
  // Where the overlay's own chrome is drawn. When the captured tree lives in an iframe,
  // that document has none of this app's styles — and injecting them would restyle the
  // very page being captured — so the outline and the comment box render out here and
  // only coordinates cross the boundary.
  const overlayDocument = frameElement ? frameElement.ownerDocument : targetDocument;

  const getFrameOffset = () => {
    if (!frameElement) return { left: 0, top: 0 };
    const { left, top } = frameElement.getBoundingClientRect();
    return { left, top };
  };

  // State is kept in the captured realm's coordinates, because that is what the capture
  // crops against; rects are shifted only on their way to the view, and pointer
  // positions only on their way in.
  const toOverlayRect = (rect) => {
    const { left, top } = getFrameOffset();
    return { left: rect.left + left, top: rect.top + top, width: rect.width, height: rect.height };
  };

  const toRootPoint = (clientX, clientY) => {
    const { left, top } = getFrameOffset();
    return { x: clientX - left, y: clientY - top };
  };

  // The capturing flag is raised together with the region, not by the effect that runs
  // the capture: in between, the comment box would paint a frame of "could not capture".
  const pick = (rect) => {
    setComment("");
    setCapture(null);
    setCaptureError(null);
    setIsCapturing(true);
    setRegion({ rect: { left: rect.left, top: rect.top, width: rect.width, height: rect.height } });
  };

  const onMouseDown = (e) => {
    e.preventDefault();
    const { x, y } = toRootPoint(e.clientX, e.clientY);
    dragOriginRef.current = { x, y };
  };

  const onMouseMove = (e) => {
    const origin = dragOriginRef.current;
    if (!origin) return;
    const { x, y } = toRootPoint(e.clientX, e.clientY);
    setDragRect(normalizeRect(origin.x, origin.y, x, y));
  };

  const onMouseUp = (e) => {

    const origin = dragOriginRef.current;
    dragOriginRef.current = null;

    if (!origin) return;

    const { x, y } = toRootPoint(e.clientX, e.clientY);
    const rect = clampRect(
      normalizeRect(origin.x, origin.y, x, y),
      rootNode.getBoundingClientRect()
    );

    setDragRect(null);

    if (rect.width < MIN_REGION_SIZE || rect.height < MIN_REGION_SIZE) return;

    pick(rect);

  };

  // The result is deliberately just the image and what the user wrote about it.
  // Everything else the overlay knows (the rect, the CSS path, the data URL) is
  // rendering detail, and the capture may have to cross the bridge into a custom
  // view — where only serializable values survive.
  const onClickSave = () =>
    onCapture?.({ screenshot: capture?.image || null, annotations: comment });

  const onClickRetryCapture = () => {
    setCaptureError(null);
    setIsCapturing(true);
    setCaptureAttempt(captureAttempt + 1);
  };

  // The screenshot is taken after the region is committed, not before, so the
  // comment box never waits on it. The overlay is excluded from the capture by
  // its data attribute, so the popup being open does not end up in the image.
  useEffect(() => {

    if (!region) return undefined;

    let isCurrent = true;

    // The tightest subtree containing the region is rasterized rather than the whole root:
    // less to serialize, and fewer chances of meeting the one node that makes the
    // serialized SVG undecodable. The full root stays as the fallback, so a subtree that
    // fails on its own still gets its shot.
    const picked = findCaptureTarget(rootNode, region.rect);

    const targets = [
      ...(picked != rootNode ? [picked] : []),
      rootNode,
    ];

    const captureFirst = async () => {

      let lastError = null;

      for (const target of targets) {
        try {
          return await captureRect({
            root: rootNode,
            target,
            rect: region.rect,
            scale: captureScale,
            backgroundColor,
          });
        } catch (error) {
          lastError = error;
          // The fallback is a strictly bigger tree, so a timeout there is a certainty,
          // not a chance: the user waits twice for the same failure.
          if (error?.isTimeout) break;
        }
      }

      throw lastError;

    };

    // The rasterization holds the main thread for as long as it takes, so it starts only
    // once the browser has painted the comment box: otherwise the spinner the user is
    // waiting on would appear when the wait is already over, and the pick looks like it
    // did nothing.
    const afterPaint = () => new Promise((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(resolve)));

    afterPaint()
      .then(captureFirst)
      .then((result) => { if (isCurrent) setCapture(result); })
      .catch((error) => {
        // A capture can still fail on an asset that taints the canvas. The
        // comment is worth keeping either way, so the flow carries on without a
        // preview instead of stranding the overlay on a spinner.
        console.error("[ScreenshotHelper] capture failed", error);
        if (isCurrent) setCaptureError(error?.message || String(error));
        // The error names no node, so the tree can be bisected to find the ones that
        // cannot be rasterized — they are what needs data-screenshot-exclude. It costs a
        // capture per node, which is exactly what just proved to be slow, so it runs
        // detached and only when someone turned it on to debug this.
        if (isCaptureDebugEnabled()) reportUncapturableNodes(rootNode);
      })
      .finally(() => { if (isCurrent) setIsCapturing(false); });

    return () => { isCurrent = false; };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region, captureAttempt]);

  useEffect(() => {

    const onKeyDown = (e) => {
      if (e.key == "Escape") onCancel?.();
    };

    targetDocument.addEventListener("keydown", onKeyDown);
    overlayDocument.addEventListener("keydown", onKeyDown);

    return () => {
      targetDocument.removeEventListener("keydown", onKeyDown);
      overlayDocument.removeEventListener("keydown", onKeyDown);
    };

  }, [targetDocument, overlayDocument, onCancel]);

  // The comment popup and the frozen outline are pinned to viewport coordinates,
  // so scrolling underneath them would break the anchoring. Picking stays
  // scrollable: the user often needs to reach the element first.
  useEffect(() => {

    const overlay = overlayRef.current;

    if (!overlay || !region) return undefined;

    const blockScroll = (e) => e.preventDefault();

    overlay.addEventListener("wheel", blockScroll, { passive: false });
    targetDocument.addEventListener("wheel", blockScroll, { passive: false });

    return () => {
      overlay.removeEventListener("wheel", blockScroll);
      targetDocument.removeEventListener("wheel", blockScroll);
    };

  }, [region, targetDocument]);

  return (
    <ScreenshotHelperView
      ref={overlayRef}
      capture={capture}
      captureError={captureError}
      comment={comment}
      dragRect={dragRect && toOverlayRect(dragRect)}
      isCapturing={isCapturing}
      overlayDocument={overlayDocument}
      overlayWindow={overlayDocument.defaultView}
      region={region && { ...region, rect: toOverlayRect(region.rect) }}
      onChangeComment={setComment}
      onClickCancel={onCancel}
      onClickRetryCapture={onClickRetryCapture}
      onClickSave={onClickSave}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
    />
  );

};

// ─── The React API ───

// Arms the closest <ScreenshotHelper> ancestor:
//
//   const { takeScreenshot } = useScreenshotHelper();
//   const result = await takeScreenshot();
//
// `takeScreenshot` resolves to `{ screenshot, annotations }` — the PNG as a Blob
// and the comment the user typed — or to null if they aborted. It never rejects:
// a capture that failed resolves with a null `screenshot` and keeps the comment,
// because what the user wrote is worth more than the image.
//
// Per-call options override the ones the provider was given:
// `takeScreenshot({ captureScale: 3 })`.
const useScreenshotHelper = () => {

  const context = useContext(ScreenshotHelperContext);

  if (!context)
    throw new Error("useScreenshotHelper must be used inside <ScreenshotHelper>");

  return context;

};

const ScreenshotHelper = ({
  backgroundColor = "auto",
  captureScale = 2,
  className = "",
  style,
  children,
}) => {

  const rootRef = useRef(null);
  // The in-flight takeScreenshot() call: its per-call options and the resolver of
  // the promise the caller is awaiting.
  const [request, setRequest] = useState(null);

  const takeScreenshot = useCallback((options = {}) =>
    new Promise((resolve) => setRequest({ options, resolve })), []);

  const finish = (result) => {
    const { resolve } = request;
    setRequest(null);
    resolve(result);
  };

  const contextValue = useMemo(() => ({ takeScreenshot }), [takeScreenshot]);

  return (
    <ScreenshotHelperContext.Provider value={contextValue}>

      <div ref={rootRef} className={className} style={style}>
        {children}
      </div>

      {request && rootRef.current &&
        <ScreenshotHelperOverlay
          backgroundColor={request.options.backgroundColor ?? backgroundColor}
          captureScale={request.options.captureScale ?? captureScale}
          frameElement={request.options.frameElement ?? null}
          rootNode={rootRef.current}
          onCancel={() => finish(null)}
          onCapture={(result) => finish(result)}
        />}

    </ScreenshotHelperContext.Provider>
  );

};

export default ScreenshotHelper;
export { ScreenshotHelper, ScreenshotHelperOverlay, useScreenshotHelper };
